import { forwardRef } from "react";
import styles from "./PoPdfView.module.css";
import { format, parseISO } from "date-fns";
import parse from "html-react-parser";
import { useSelector } from "react-redux";

const PoPdfView = forwardRef(({ pdfData }, ref) => {
  const data = useSelector((state) => state.loginData.value);

  const {
    issuer_details = {},
    vendor_details = {},
    billing_details = {},
    shipping_details = {},
    pricing = {},
    terms = {},
    item_list = [],
    company_vendor_code = "",
    delivery_date = "",
    payment_date = "",
  } = pdfData;

  const addressFormate = (address) =>
    address ? Object.values(address).filter(Boolean).join(", ") : "-";

  const dateFormate = (date) =>
    date ? format(parseISO(date), "dd/MM/yyyy") : "-";

  const {
    issuer_details_name,
    issuer_details_email,
    issuer_details_PAN,
    issuer_details_GSTIN,
    issuer_details_CIN,
    issuer_details_purchase_order_no,
    issuer_details_purchase_order_date,
    issuer_details_address,
  } = issuer_details;

  const { vendor_details_address, vendor_image_base64 } = vendor_details;

  const { billing_details_name, billing_details_address } = billing_details;
  const { shipping_details_name, shipping_details_address } = shipping_details;

  const normalizedTerms = Array.isArray(terms) ? terms[0] || {} : terms;
  const {
    payment_terms,
    validity,
    delivery_terms,
    mode_of_transportation,
    terms_and_conditions_of_purchase = "",
  } = normalizedTerms;

  let grandTotal = 0;
  let CurrencyType = "";

  const itemList = item_list.map((item) => {
    grandTotal += Number(item.total_price || 0);
    CurrencyType = item.currency_type;
    return {
      ...item,
      UnitPrice: Number(item.unit_price).toFixed(2),
      itemTotalPrice: Number(item.total_price).toFixed(2),
    };
  });

  return (
    <div className={styles.poPdfContainer} ref={ref}>
      <table className={`${styles.removeDefaultStyle} ${styles.borderColapse}`}>
        <tbody>
          {/* HEADER */}
          <tr>
            <td>
              <h1 className={styles.header}>PURCHASE ORDER</h1>
            </td>
          </tr>

          {/* COMPANY */}
          <tr>
            <td>
              <div className={styles.companyRow}>
                {vendor_image_base64 && (
                  <img src={vendor_image_base64} className={styles.logo} />
                )}
                <div>
                  <p>
                    <strong>{issuer_details_name}</strong>
                  </p>
                  <p>{addressFormate(issuer_details_address)}</p>
                  <p>E-mail : {issuer_details_email}</p>
                </div>
              </div>
            </td>
          </tr>

          {/* PAN / GST / CIN */}
          <tr>
            <td>
              <ul className={styles.issuerDetailsRegisterNumber}>
                <li>
                  <strong>PAN NO :</strong> AZTPK9087R
                </li>
                <li>
                  <strong>GSTIN :</strong> 33AAGCT8123JIX9
                </li>
                <li>
                  <strong>CIN NO :</strong> L17111MH1973PLC01970
                </li>
              </ul>
            </td>
          </tr>

          {/* 🔥 PO / DATE – FIXED ALIGNMENT */}
          <tr>
            <td>
              <table
                className={`${styles.removeDefaultStyle} ${styles.borderColapse}`}
              >
                <tbody>
                  <tr>
                    <td>
                      <strong>Purchase Order No :</strong>
                      <br />
                      {issuer_details_purchase_order_no}
                    </td>
                    <td>
                      <strong>Purchase Order Date :</strong>
                      <br />
                      {dateFormate(issuer_details_purchase_order_date)}
                    </td>
                    <td>
                      <strong>Delivery Date :</strong>
                      <br />
                      {delivery_date}
                    </td>
                    <td>
                      <strong>Payment Date :</strong>
                      <br />
                      {payment_date}
                    </td>
                  </tr>
                </tbody>
              </table>
            </td>
          </tr>

          {/* VENDOR / BILLING / SHIPPING */}
          <tr>
            <td>
              <table
                className={`${styles.removeDefaultStyle} ${styles.borderColapse}`}
              >
                <tbody>
                  <tr className={styles.cardTableHeader}>
                    <td>Vendor Details</td>
                    <td>Billing Details</td>
                    <td>Shipping Details</td>
                  </tr>
                  <tr>
                    <td>
                      {company_vendor_code}
                      <br />
                      {addressFormate(vendor_details_address)}
                    </td>
                    <td>
                      {billing_details_name}
                      <br />
                      {addressFormate(billing_details_address)}
                    </td>
                    <td>
                      {shipping_details_name}
                      <br />
                      {addressFormate(shipping_details_address)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </td>
          </tr>

          {/* ITEM TABLE */}
          <tr>
            <td>
              <table
                className={`${styles.customItemListTable} ${styles.borderColapse}`}
              >
                <thead>
                  <tr>
                    <th>S.N</th>
                    <th>Part No</th>
                    <th>Description of Goods</th>
                    <th>Item Name</th>
                    <th>Item code</th>
                    <th>HSN Code</th>
                    <th>Qty</th>
                    <th>UOM</th>
                    <th>Rate({CurrencyType})</th>
                    <th>Tax%</th>
                    <th>Taxable Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {itemList.map((i, idx) => (
                    <tr key={idx}>
                      <td>{idx + 1}</td>
                      <td>{i.part_number}</td>
                      <td>{i.description}</td>
                      <td>{i.item_name}</td>
                      <td>{i.item_code}</td>
                      <td>{i.hsn_code}</td>
                      <td>{i.quantity}</td>
                      <td>{i.unit}</td>
                      <td>{i.UnitPrice}</td>
                      <td>{Number(i.tax)}</td>
                      <td>{i.itemTotalPrice}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan="10" style={{ textAlign: "right" }}>
                      <strong>Grand Total :</strong>
                    </td>
                    <td>
                      {grandTotal.toFixed(2)} {CurrencyType}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </td>
          </tr>

          {/* TERMS */}
          <tr className={styles.terms}>
            <td>
              <div className={styles.termsTitle}>Terms & Conditions</div>

              <ul className={styles.termsList}>
                <li>
                  <strong>Payment Terms:</strong> {payment_terms}
                </li>
                <li>
                  <strong>Validity:</strong> {validity}
                </li>
                <li>
                  <strong>Delivery Terms:</strong> {delivery_terms}
                </li>
                <li>
                  <strong>Transport:</strong> {mode_of_transportation}
                </li>
              </ul>

              <div className={styles.termsParsed}>
                {parse(terms_and_conditions_of_purchase)}
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      {/* FOOTER */}
      <div className={styles.pdfFooter}>
        This is a computer-generated document. No signature is required.
      </div>
    </div>
  );
});

export default PoPdfView;
